import { LoadingState } from "@/components/ui/loading-state";
import { useState } from "react";
import { ChevronDown, Download, FileSpreadsheet, Loader2 } from "lucide-react";

import type {
  BasisFinancialSummaryPeriod,
  BasisOption,
  BasisOptionComponent,
  BasisOptionStatementPlan,
} from "@/lib/api/databook";
import type { BasisOptionsLoadState } from "./basisOptions";

function sourceTone(tableType: string): string {
  if (
    tableType.includes("eliminations") ||
    tableType.includes("reconciliation")
  ) {
    return "border-amber-500/30 bg-amber-500/10 text-amber-800";
  }
  if (tableType.includes("balance_sheet")) {
    return "border-emerald-500/30 bg-emerald-500/10 text-emerald-700";
  }
  if (tableType.includes("pnl")) {
    return "border-blue-500/30 bg-blue-500/10 text-blue-700";
  }
  return "border-violet-500/30 bg-violet-500/10 text-violet-700";
}

function displayMonth(value: string): string {
  const [year, month] = value.split("-").map(Number);
  return new Intl.DateTimeFormat("en", {
    month: "short",
    year: "2-digit",
    timeZone: "UTC",
  }).format(new Date(Date.UTC(year, month - 1, 1)));
}

function displayMonthRanges(values: string[]): string {
  const periods = [...new Set(values)].sort();
  const ranges: string[] = [];
  let rangeStart = periods[0];
  let previous = periods[0];
  const monthIndex = (value: string) => {
    const [year, month] = value.split("-").map(Number);
    return year * 12 + month;
  };

  for (const period of periods.slice(1)) {
    if (monthIndex(period) === monthIndex(previous) + 1) {
      previous = period;
      continue;
    }
    ranges.push(
      rangeStart === previous
        ? displayMonth(rangeStart)
        : `${displayMonth(rangeStart)}–${displayMonth(previous)}`,
    );
    rangeStart = period;
    previous = period;
  }
  if (rangeStart) {
    ranges.push(
      rangeStart === previous
        ? displayMonth(rangeStart)
        : `${displayMonth(rangeStart)}–${displayMonth(previous)}`,
    );
  }
  return ranges.join(", ");
}

function displayYearRanges(values: string[]): string {
  const years = [...new Set(values)].map(Number).sort((left, right) => left - right);
  const ranges: string[] = [];
  let rangeStart = years[0];
  let previous = years[0];
  const displayFiscalYear = (year: number) => `FY${String(year).slice(-2)}`;

  for (const year of years.slice(1)) {
    if (year === previous + 1) {
      previous = year;
      continue;
    }
    ranges.push(
      rangeStart === previous
        ? displayFiscalYear(rangeStart)
        : `${displayFiscalYear(rangeStart)}–${displayFiscalYear(previous)}`,
    );
    rangeStart = year;
    previous = year;
  }
  if (rangeStart) {
    ranges.push(
      rangeStart === previous
        ? displayFiscalYear(rangeStart)
        : `${displayFiscalYear(rangeStart)}–${displayFiscalYear(previous)}`,
    );
  }
  return ranges.join(", ");
}

function displayPeriodUsage(
  periodUsage: BasisOptionComponent["period_usage"],
): string[] {
  const usage: string[] = [];
  if (periodUsage.months.length > 0) {
    usage.push(`Monthly: ${displayMonthRanges(periodUsage.months)}`);
  }
  if (periodUsage.years.length > 0) {
    usage.push(`Annual: ${displayYearRanges(periodUsage.years)}`);
  }
  return usage.length > 0 ? usage : ["Period not identified"];
}

function basisComponentPeriodUsage(
  component: BasisOptionComponent,
): string[] {
  return displayPeriodUsage(component.period_usage);
}

function statementPlanPeriodUsage(
  sourceComponents: BasisOptionComponent[],
): string[] {
  return displayPeriodUsage({
    months: sourceComponents.flatMap(
      (component) => component.period_usage.months,
    ),
    years: sourceComponents.flatMap(
      (component) => component.period_usage.years,
    ),
  });
}

function basisOptionCoverage(option: BasisOption): string {
  const sourceComponents = [option.pnl_plan, option.balance_sheet_plan].flatMap(
    (plan) => plan?.source_components ?? [],
  );
  return statementPlanPeriodUsage(sourceComponents).join(" · ");
}

function SourcePill({
  component,
  onDownload,
}: {
  component: BasisOptionComponent;
  onDownload: (component: BasisOptionComponent) => void;
}) {
  const tableType = component.tables[0]?.table_type ?? "";
  const periodUsage = basisComponentPeriodUsage(component);
  return (
    <span
      className={`inline-flex max-w-[280px] items-center rounded-md border px-2 py-1.5 ${sourceTone(
        tableType,
      )}`}
      title={component.label}
    >
      <span className="flex min-w-0 flex-col">
        <span className="truncate font-semibold" style={{ fontSize: 10.5 }}>
          {component.label}
        </span>
        {periodUsage.map((period) => (
          <span
            key={period}
            className="truncate font-medium opacity-70"
            style={{ fontSize: 9 }}
          >
            {period}
          </span>
        ))}
      </span>
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          onDownload(component);
        }}
        title={`Download sources for ${component.label}`}
        aria-label={`Download sources for ${component.label}`}
        className="-mr-1 ml-1 shrink-0 rounded p-0.5 opacity-70 hover:bg-black/10 hover:opacity-100"
      >
        <Download aria-hidden="true" size={10} />
      </button>
    </span>
  );
}

function SourceFormula({
  sourceComponents,
  resultLabel,
  onDownload,
}: {
  sourceComponents: BasisOptionComponent[];
  resultLabel: string;
  onDownload: (component: BasisOptionComponent) => void;
}) {
  const resultPeriodUsage = statementPlanPeriodUsage(sourceComponents);
  return (
    <div className="flex flex-wrap items-stretch gap-1.5">
      {sourceComponents.map((component, index) => (
        <span key={component.label} className="contents">
          {index > 0 && (
            <span
              className="self-center font-semibold text-muted-foreground"
              style={{ fontSize: 12 }}
            >
              +
            </span>
          )}
          <SourcePill
            component={component}
            onDownload={onDownload}
          />
        </span>
      ))}
      <span
        className="self-center font-semibold text-muted-foreground"
        style={{ fontSize: 12 }}
      >
        =
      </span>
      <span
        className="inline-flex items-center rounded-md border border-foreground bg-foreground px-2 py-1 font-semibold text-background"
      >
        <span className="flex min-w-0 flex-col">
          <span className="truncate font-semibold" style={{ fontSize: 10.5 }}>
            {resultLabel}
          </span>
          {resultPeriodUsage.map((period) => (
            <span
              key={period}
              className="truncate font-medium opacity-70"
              style={{ fontSize: 9 }}
            >
              {period}
            </span>
          ))}
        </span>
      </span>
    </div>
  );
}

function StatementPlan({
  resultLabel,
  plan,
  onDownload,
}: {
  resultLabel: string;
  plan: BasisOptionStatementPlan;
  onDownload: (component: BasisOptionComponent) => void;
}) {
  return (
    <SourceFormula
      sourceComponents={plan.source_components}
      resultLabel={resultLabel}
      onDownload={onDownload}
    />
  );
}

interface OptionSourceFile {
  document_id: string;
  filename: string;
  sheets: string[];
}

function optionSourceFiles(option: BasisOption): OptionSourceFile[] {
  const files = new Map<string, { filename: string; sheets: Set<string> }>();
  for (const plan of [option.pnl_plan, option.balance_sheet_plan]) {
    if (!plan) continue;
    for (const component of plan.source_components) {
      for (const source of component.tables) {
        const file = files.get(source.document_id) ?? {
          filename: source.filename,
          sheets: new Set<string>(),
        };
        file.sheets.add(source.source_sheet_name);
        files.set(source.document_id, file);
      }
    }
  }
  return Array.from(files, ([document_id, file]) => ({
    document_id,
    filename: file.filename,
    sheets: Array.from(file.sheets),
  }));
}

const COLLAPSED_SHEET_COUNT = 4;

function SourceFileCard({
  source,
  onDownload,
}: {
  source: OptionSourceFile;
  onDownload: (source: { document_id: string; filename: string }) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const hiddenSheetCount = Math.max(
    source.sheets.length - COLLAPSED_SHEET_COUNT,
    0,
  );
  const visibleSheets = expanded
    ? source.sheets
    : source.sheets.slice(0, COLLAPSED_SHEET_COUNT);

  return (
    <div className="min-w-[260px] flex-1 rounded-lg border border-border bg-background/70 p-2.5">
      <div className="flex min-w-0 items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-1.5">
          <FileSpreadsheet
            aria-hidden="true"
            className="shrink-0 text-muted-foreground"
            size={12}
          />
          <span
            className="truncate font-medium text-foreground/80"
            style={{ fontSize: 10.5 }}
            title={source.filename}
          >
            {source.filename}
          </span>
        </div>
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            onDownload(source);
          }}
          title={`Download ${source.filename}`}
          aria-label={`Download ${source.filename}`}
          className="shrink-0 rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <Download aria-hidden="true" size={11} />
        </button>
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-1">
        <span
          className="mr-0.5 font-semibold uppercase tracking-wider text-muted-foreground"
          style={{ fontSize: 8.5 }}
        >
          Sheets
        </span>
        {visibleSheets.map((sheet) => (
          <span
            key={sheet}
            className="max-w-[220px] truncate rounded border border-border bg-muted/60 px-1.5 py-0.5 text-foreground/70"
            style={{ fontSize: 9.5 }}
            title={sheet}
          >
            {sheet}
          </span>
        ))}
        {hiddenSheetCount > 0 && !expanded ? (
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              setExpanded(true);
            }}
            className="inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 font-medium text-muted-foreground hover:bg-muted hover:text-foreground"
            style={{ fontSize: 9.5 }}
          >
            +{hiddenSheetCount} more
            <ChevronDown aria-hidden="true" size={9} />
          </button>
        ) : null}
        {expanded && hiddenSheetCount > 0 ? (
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              setExpanded(false);
            }}
            className="rounded px-1.5 py-0.5 font-medium text-muted-foreground hover:bg-muted hover:text-foreground"
            style={{ fontSize: 9.5 }}
          >
            Show less
          </button>
        ) : null}
      </div>
    </div>
  );
}

function OptionSources({
  sourceFiles,
  onDownload,
}: {
  sourceFiles: OptionSourceFile[];
  onDownload: (source: { document_id: string; filename: string }) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const sheetCount = sourceFiles.reduce(
    (total, source) => total + source.sheets.length,
    0,
  );
  const fileLabel = `${sourceFiles.length} ${
    sourceFiles.length === 1 ? "file" : "files"
  }`;
  const sheetLabel = `${sheetCount} ${sheetCount === 1 ? "sheet" : "sheets"}`;

  return (
    <div className="border-t border-border">
      <button
        type="button"
        aria-expanded={expanded}
        onClick={(event) => {
          event.stopPropagation();
          setExpanded((current) => !current);
        }}
        className="flex w-full items-center px-4 py-3 text-left hover:bg-muted/40"
      >
        <span className="ml-[30px] flex min-w-0 flex-1 items-center justify-between gap-3">
          <span
            className="font-semibold uppercase tracking-wider text-muted-foreground"
            style={{ fontSize: 9 }}
          >
            Sources
          </span>
          <span className="flex shrink-0 items-center gap-2 text-muted-foreground">
            <span style={{ fontSize: 10.5 }}>
              {fileLabel} · {sheetLabel}
            </span>
            <ChevronDown
              aria-hidden="true"
              className={`transition-transform ${expanded ? "rotate-180" : ""}`}
              size={14}
            />
          </span>
        </span>
      </button>

      {expanded ? (
        <div className="border-t border-border px-4 py-3">
          <div className="ml-[30px] flex flex-wrap items-start gap-2">
            {sourceFiles.map((source) => (
              <SourceFileCard
                key={source.document_id}
                source={source}
                onDownload={onDownload}
              />
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function basisOptionNoteItems(option: BasisOption): string[] {
  const notes = option.notes?.trim() || option.how;
  return notes
    .split(/\r?\n/)
    .map((line) => line.replace(/^\s*[-•]\s*/, "").trim())
    .filter(Boolean);
}

const VARIANCE_TO_RECOMMENDED_PREFIX = "Variance to recommended basis:";
const STATEMENT_BASIS_NOTE_PATTERN =
  /^(?:p\s*&\s*l|pnl|balance\s+sheet|bs)\s+basis\s*:/i;

function isVarianceToRecommendedNote(item: string): boolean {
  return item
    .toLowerCase()
    .startsWith(VARIANCE_TO_RECOMMENDED_PREFIX.toLowerCase());
}

function isStatementBasisNote(item: string): boolean {
  return STATEMENT_BASIS_NOTE_PATTERN.test(item);
}

function basisOptionVarianceToRecommended(option: BasisOption): string {
  const variance = basisOptionNoteItems(option).find(
    isVarianceToRecommendedNote,
  );
  if (!variance) {
    throw new Error(
      `Basis option ${option.id} is missing "${VARIANCE_TO_RECOMMENDED_PREFIX}" in how`,
    );
  }
  return variance.slice(VARIANCE_TO_RECOMMENDED_PREFIX.length).trim();
}

function BasisOptionSummary({ option }: { option: BasisOption }) {
  const noteItems = basisOptionNoteItems(option).filter(
    (item) =>
      !isVarianceToRecommendedNote(item) && !isStatementBasisNote(item),
  );
  return (
    <div className="mt-4 overflow-hidden rounded-lg border border-border bg-background/70">
      <div className="px-3 py-2.5">
        <div className="font-semibold uppercase tracking-wider text-muted-foreground" style={{ fontSize: 8.5 }}>
          Notes
        </div>
        <ul
          className="mt-1.5 list-disc space-y-1 pl-4 text-foreground/75"
          style={{ fontSize: 10.5, lineHeight: 1.55 }}
        >
          {noteItems.map((item, index) => (
            <li
              key={`${index}-${item}`}
              className={
                /^(coverage gaps|perimeter changes):/i.test(item)
                  ? "rounded border border-orange-200 bg-orange-100 px-2 py-1 text-orange-700"
                  : undefined
              }
            >
              {item}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function financialSummaryPeriodLabel(
  period: BasisFinancialSummaryPeriod,
): string {
  const actualCount = period.actual_months.length;
  const budgetCount = period.budget_months.length;
  const forecastCount = period.forecast_months.length;
  const fiscalYear = `FY${String(period.fiscal_year).slice(-2)}`;
  if (actualCount + budgetCount + forecastCount === 0) return fiscalYear;

  const marker = budgetCount > 0 ? "B" : forecastCount > 0 ? "F" : "";
  if (!marker) return fiscalYear;
  const composition = [
    actualCount > 0 ? `${actualCount}A` : null,
    budgetCount > 0 ? `${budgetCount}B` : null,
    forecastCount > 0 ? `${forecastCount}F` : null,
  ].filter(Boolean).join("+");
  return `${fiscalYear}${marker} ${composition}`;
}

function formatFinancialSummaryValue(value: number | null): string {
  if (value === null) return "—";
  const absoluteValue = Math.abs(value);
  const sign = value < 0 ? "−" : "";
  if (absoluteValue >= 1_000_000) {
    return `${sign}${(absoluteValue / 1_000_000).toFixed(1)}m`;
  }
  if (absoluteValue >= 1_000) {
    return `${sign}${(absoluteValue / 1_000).toFixed(1)}k`;
  }
  return `${sign}${new Intl.NumberFormat("en", {
    maximumFractionDigits: 1,
  }).format(absoluteValue)}`;
}

function BasisFinancialSummary({
  periods,
  presentationCurrency,
}: {
  periods: BasisFinancialSummaryPeriod[];
  presentationCurrency: string | null;
}) {
  return (
    <details className="group/financial-summary mt-4 border-t border-border">
      <summary className="flex cursor-pointer list-none items-center justify-between gap-2 border-b border-border py-2.5 text-xs font-semibold text-foreground hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring [&::-webkit-details-marker]:hidden">
        <span>Financial summary</span>
        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded border border-border bg-background">
          <ChevronDown aria-hidden="true" size={16} className="transition-transform group-open/financial-summary:rotate-180" />
        </span>
      </summary>
      <div className="pb-2 pt-3">
        {presentationCurrency ? (
          <div
            className="mb-1 text-right font-medium text-muted-foreground"
            style={{ fontSize: 9.5 }}
          >
            {presentationCurrency}
          </div>
        ) : null}
        <table className="w-full table-fixed border-collapse" style={{ fontSize: 11.5 }}>
          <thead>
            <tr className="text-muted-foreground" style={{ fontSize: 10 }}>
              <th className="py-1 text-left font-medium">Period</th>
              <th className="py-1 text-center font-medium">Revenue</th>
              <th className="py-1 text-center font-medium">Net profit</th>
              <th className="py-1 text-center font-medium">Net assets</th>
            </tr>
          </thead>
          <tbody className="tabular-nums">
            {periods.map((period) => (
              <tr key={period.fiscal_year}>
                <td className="py-1 text-left text-muted-foreground">
                  {financialSummaryPeriodLabel(period)}
                </td>
                <td className="py-1 text-center">
                  {formatFinancialSummaryValue(period.revenue)}
                </td>
                <td className="py-1 text-center">
                  {formatFinancialSummaryValue(period.net_profit)}
                </td>
                <td className="py-1 text-center">
                  {formatFinancialSummaryValue(period.net_assets)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </details>
  );
}

interface BasisOfPreparationStageProps {
  state: BasisOptionsLoadState;
  selectedOptionId: string | null;
  onSelectionChange: (optionId: string) => void;
  onRetry: () => void;
  onDownloadComponent: (component: BasisOptionComponent) => void;
  onDownloadSource: (source: {
    document_id: string;
    filename: string;
  }) => void;
  onBack: () => void;
  onNext: () => void;
  nextStageConnected?: boolean;
  nextDisabled?: boolean;
  nextPendingMessage?: string;
  nextLabel?: string;
}

export function BasisOfPreparationStage({
  state,
  selectedOptionId,
  onSelectionChange,
  onRetry,
  onDownloadComponent,
  onDownloadSource,
  onBack,
  onNext,
  nextStageConnected = false,
  nextDisabled = false,
  nextPendingMessage,
  nextLabel,
}: BasisOfPreparationStageProps) {
  const [expandedOptionOverrides, setExpandedOptionOverrides] = useState<
    Record<string, boolean>
  >({});

  if (state.status === "loading" || state.status === "pending_refresh") {
    return <LoadingState />;
  }

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="builder-stage-frame pb-20 pt-10">
        <div className="mb-6 font-semibold" style={{ fontSize: 20 }}>
          Basis of preparation
        </div>

        {state.status === "empty" && (
          <div className="rounded-xl border border-border bg-card px-4 py-8 text-center">
            <div className="font-semibold" style={{ fontSize: 13 }}>
              No source-backed consolidation options were identified
            </div>
            <div className="text-muted-foreground mt-1" style={{ fontSize: 11.5 }}>
              No default approach was invented where the analyzed sources
              did not support one.
            </div>
          </div>
        )}

        {state.status === "unavailable" && (
          <div className="rounded-xl border border-border bg-card px-4 py-6 text-center">
            <div className="font-semibold" style={{ fontSize: 13 }}>
              Consolidation options are not available yet
            </div>
            <div className="text-muted-foreground mt-1" style={{ fontSize: 11.5 }}>
              Complete the current source review, then try again.
            </div>
            <button
              onClick={onRetry}
              className="mt-3 rounded-lg border border-border px-3 py-1.5 font-medium hover:bg-muted"
              style={{ fontSize: 12.5 }}
            >
              Try again
            </button>
          </div>
        )}

        {state.status === "refresh_failed" && (
          <div className="rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-6 text-center">
            <div className="font-semibold text-destructive" style={{ fontSize: 13 }}>
              The Basis could not be updated
            </div>
            {state.error && (
              <div className="mt-1 text-muted-foreground" style={{ fontSize: 11.5 }}>
                {state.error}
              </div>
            )}
            <button
              onClick={onRetry}
              className="mt-3 rounded-lg bg-foreground px-3 py-1.5 font-medium text-background"
              style={{ fontSize: 12.5 }}
            >
              Retry
            </button>
          </div>
        )}

        {state.status === "needs_review" && (
          <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 px-4 py-6">
            <div className="font-semibold text-amber-800" style={{ fontSize: 13 }}>
              The Basis needs source review
            </div>
            <div className="mt-1 text-muted-foreground" style={{ fontSize: 11.5 }}>
              The saved scope cannot be applied safely to the selected financial sources.
            </div>
            {state.issues.length > 0 && (
              <ul className="mt-3 list-disc space-y-1 pl-4 text-muted-foreground" style={{ fontSize: 11.5 }}>
                {state.issues.map((issue) => (
                  <li key={`${issue.code}:${issue.table_keys.join(",")}`}>
                    {issue.message}
                  </li>
                ))}
              </ul>
            )}
            <button
              onClick={onRetry}
              className="mt-3 rounded-lg border border-border px-3 py-1.5 font-medium hover:bg-muted"
              style={{ fontSize: 12.5 }}
            >
              Check again
            </button>
          </div>
        )}

        {state.status === "failed" && (
          <div className="rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-6 text-center">
            <div className="font-semibold text-destructive" style={{ fontSize: 13 }}>
              {state.invalidArtifact
                ? "The saved arbitration result is invalid"
                : "Could not load consolidation options"}
            </div>
            <button
              onClick={onRetry}
              className="mt-3 rounded-lg bg-foreground text-background px-3 py-1.5 font-medium"
              style={{ fontSize: 12.5 }}
            >
              Retry
            </button>
          </div>
        )}

        {state.status === "loaded" && (
          <div className="space-y-2.5">
            {state.options.map((option) => {
              const selected = option.id === selectedOptionId;
              const expansionKey = [
                state.sourceRevisionId,
                state.legalStructureRevisionId,
                option.id,
              ].join(":");
              const expanded =
                expandedOptionOverrides[expansionKey] ?? false;
              const sourceFiles = expanded ? optionSourceFiles(option) : [];
              return (
                <article
                  key={option.id}
                  className={`overflow-hidden rounded-xl border-2 transition-colors ${
                    selected
                      ? "border-foreground bg-foreground/[0.03]"
                      : "border-border bg-card hover:border-muted-foreground/40"
                  }`}
                >
                  <div className="p-4 text-left">
                    <div className="flex w-full items-start gap-3">
                      <button
                        type="button"
                        aria-label={`Select ${option.title}`}
                        aria-pressed={selected}
                        onClick={() => onSelectionChange(option.id)}
                        className={`mt-0.5 flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full ${
                          selected
                            ? "bg-foreground text-background"
                            : "bg-muted text-muted-foreground"
                        }`}
                        style={{ fontSize: 10 }}
                      >
                        {selected ? "✓" : "·"}
                      </button>
                      <div className="min-w-0 flex-1">
                        <button
                          type="button"
                          aria-label={`${expanded ? "Collapse" : "Expand"} ${
                            option.title
                          }`}
                          aria-expanded={expanded}
                          onClick={() =>
                            setExpandedOptionOverrides((current) => ({
                              ...current,
                              [expansionKey]: !current[expansionKey],
                            }))
                          }
                          className="w-full text-left"
                        >
                          <div className="flex flex-wrap items-center gap-2">
                            <span
                              className="font-semibold text-foreground"
                              style={{ fontSize: 13 }}
                            >
                              {option.title}
                            </span>
                            {option.recommended ? (
                              <span
                                className={`whitespace-nowrap rounded-full px-2 py-0.5 font-semibold ${
                                  selected
                                    ? "bg-foreground text-background"
                                    : "bg-muted text-muted-foreground"
                                }`}
                                style={{ fontSize: 9.5 }}
                              >
                                Recommended
                              </span>
                            ) : null}
                            <ChevronDown
                              aria-hidden="true"
                              className={`ml-auto shrink-0 text-muted-foreground transition-transform ${
                                expanded ? "rotate-180" : ""
                              }`}
                              size={16}
                            />
                          </div>
                        </button>
                        {!expanded ? (
                          <div className="mt-1.5 select-text space-y-1 text-muted-foreground">
                            <div style={{ fontSize: 10.5 }}>
                              Coverage: {basisOptionCoverage(option)}
                            </div>
                            <div style={{ fontSize: 10.5 }}>
                              Differs by: {basisOptionVarianceToRecommended(option)}
                            </div>
                          </div>
                        ) : null}
                      </div>
                    </div>

                    {expanded ? (
                      <div className="ml-[30px] min-w-0">
                        <div className="mt-3 space-y-3">
                          {option.pnl_plan && (
                            <StatementPlan
                              resultLabel={
                                option.pnl_plan.mode === "reported"
                                  ? "Reported P&L"
                                  : "Consolidated P&L"
                              }
                              plan={option.pnl_plan}
                              onDownload={onDownloadComponent}
                            />
                          )}
                          {option.balance_sheet_plan && (
                            <div
                              className={
                                option.pnl_plan
                                  ? "border-t border-border pt-3"
                                  : undefined
                              }
                            >
                              <StatementPlan
                                resultLabel={
                                  option.balance_sheet_plan.mode === "reported"
                                    ? "Reported balance sheet"
                                    : "Consolidated balance sheet"
                                }
                                plan={option.balance_sheet_plan}
                                onDownload={onDownloadComponent}
                              />
                            </div>
                          )}
                        </div>

                        <BasisFinancialSummary
                          periods={option.financial_summary}
                          presentationCurrency={state.presentationCurrency}
                        />
                        <BasisOptionSummary option={option} />
                      </div>
                    ) : null}
                  </div>
                  {expanded ? (
                    <OptionSources
                      sourceFiles={sourceFiles}
                      onDownload={onDownloadSource}
                    />
                  ) : null}
                </article>
              );
            })}
          </div>
        )}

        <div className="mt-8 flex items-center justify-between gap-3">
          <button
            onClick={onBack}
            className="rounded-lg border border-border px-4 py-2 font-medium text-foreground/80 hover:bg-muted"
            style={{ fontSize: 12.5 }}
          >
            ← Back to legal entities
          </button>
          <div className="flex flex-col items-end gap-2">
            {nextPendingMessage && (
              <div
                role="status"
                className="flex items-center gap-1.5 text-muted-foreground"
                style={{ fontSize: 11.5 }}
              >
                <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                <span>{nextPendingMessage}</span>
              </div>
            )}
            <button
              onClick={onNext}
              disabled={
                !nextStageConnected
                || nextDisabled
                || selectedOptionId === null
              }
              className="rounded-lg bg-foreground px-4 py-2 font-medium text-background hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
              style={{ fontSize: 12.5 }}
            >
              {nextStageConnected
                ? nextLabel ?? "Continue →"
                : "File review not connected yet"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
